import React, { useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { homeCopy } from '@/contexts/language/translations/pages/home';

const SolutionsSection = () => {
  const { t, language } = useLanguage();
  const c = homeCopy[language].solutions;
  const [activeTab, setActiveTab] = useState(0);
  
  const solutions = [
    {
      id: 'ai',
      title: t('solutionAITitle'),
      description: t('solutionAIDesc'),
      features: [
        ...(c.aiDataFeature ? [c.aiDataFeature] : []),
        t('solutionAIFeature1'),
        t('solutionAIFeature2'),
        t('solutionAIFeature3'),
        t('solutionAIFeature4'),
      ],
      image: '/lovable-uploads/talk_to_your_data_with_ai.png',
      link: '/our-work/context-engineering',
    },
    {
      id: 'ras-saas',
      title: t('solutionRasSaasTitle'),
      description: t('solutionRasSaasDesc'),
      features: [
        t('solutionRasSaasFeature1'),
        t('solutionRasSaasFeature2'),
        t('solutionRasSaasFeature3'),
        t('solutionRasSaasFeature4'),
        t('solutionRasSaasFeature5'),
      ],
      image: '/lovable-uploads/ras(saas).png',
      link: '/solutions/retail-automation-suite',
    },
    {
      id: 'data-warehousing',
      title: t('solutionDataWarehousingTitle'),
      description: t('solutionDataWarehousingDesc'),
      features: [
        t('solutionDataWarehousingFeature1'),
        t('solutionDataWarehousingFeature2'),
        t('solutionDataWarehousingFeature3'),
      ],
      image: '/lovable-uploads/data_warehous.png',
      link: '/our-work/data-engineering',
    },
    {
      id: 'reporting-bi',
      title: t('solutionReportingBITitle'),
      description: t('solutionReportingBIDesc'),
      features: [
        t('solutionReportingBIFeature1'),
        t('solutionReportingBIFeature2'),
        t('solutionReportingBIFeature3'),
      ],
      image: '/lovable-uploads/reporting_and_bi.png',
      link: '/our-work/retail-profit-management',
    },
    {
      id: 'automations',
      title: t('solutionAutomationsTitle'),
      description: t('solutionAutomationsDesc'),
      features: [
        t('solutionAutomationsFeature1'),
        t('solutionAutomationsFeature2'),
        t('solutionAutomationsFeature3'),
      ],
      image: '/lovable-uploads/automations_1.png',
      link: null,
    },
  ];

  return (
    <section id="solutions" className="py-20 bg-white">
      <div className="container-custom max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-block px-4 py-2 rounded-lg bg-foreground text-white text-xs font-semibold mb-6">
            {t('solutionsBadge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
            <span className="text-foreground">{t('servicesTitlePrefix')}</span>
            <span className="text-[#FF6B35]">{t('servicesTitleHighlight')}</span>
          </h2>
          <p className="text-muted-foreground mb-6">
            {t('solutionsSubheading')}
          </p>
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-1 text-sm font-medium text-[#FF6B35] hover:underline"
          >
            {t('solutionsHowItWorksLink')}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

        {/* Tabs - matching design with solution names */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex rounded-xl bg-[#FFDEAD] p-1 gap-2 md:gap-3 flex-wrap justify-center">
            {solutions.map((solution, index) => (
              <button
                key={solution.id}
                onClick={() => setActiveTab(index)}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                  activeTab === index
                    ? 'bg-[#FFE5B4] text-white' // Yellow-orange background with white text for active tab
                    : 'text-foreground bg-[#FFDEAD] hover:bg-[#FFDEAD]'
                }`}
              >
                {solution.title}
              </button>
            ))}
          </div>
        </div>

        {/* Active Solution Content */}
        <div className="max-w-7xl mx-auto">
          {solutions.map((solution, index) => (
            <div
              key={solution.id}
              className={`${activeTab === index ? 'block' : 'hidden'}`}
            >
              {/* Content section - text always left, image always right */}
              <div className="grid md:grid-cols-2 gap-12 items-center">
                {/* Content - always on left */}
                <div className="text-left">
                  <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                    {solution.title}
                  </h3>
                  <p className="text-muted-foreground mb-8 leading-relaxed text-base">
                    {solution.description}
                  </p>
                  <ul className="space-y-4">
                    {solution.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#FFE5B4] flex items-center justify-center flex-shrink-0 mt-0.5">
                        </div>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Image - always on right: blue card same height for all, image fills area, cropped from right if needed (left visible) */}
                <div className="order-first md:order-last relative">
                  <div
                    className="relative h-[420px] rounded-2xl overflow-hidden"
                    style={{ backgroundColor: 'rgb(224, 231, 255)' }}
                  >
                    <div className="absolute left-6 top-6 right-0 bottom-0 rounded-2xl overflow-hidden">
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-full rounded-2xl object-cover object-left-bottom shadow-sm"
                      />
                    </div>
                  </div>
                  {solution.link && (
                    <div className="mt-6 text-center">
                      <Link
                        to={solution.link}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#FF6B35] text-white font-semibold hover:bg-[#FF6B35]/90 transition-colors"
                      >
                        {c.exploreMore}
                        <span aria-hidden="true">&rarr;</span>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default SolutionsSection;

